import { showToast } from '../../engine/toast';
import { t } from '../../engine/i18n/i18n';
import { equipTitle, type TitleRarity, type TitleView } from './profile-api';
import { fetchStoreBalance, purchaseTitle } from '../shop/shop-api';

/**
 * Title picker sheet, opened from the player card's "Change title" button.
 *
 * Follows send-army-modal.ts's idiom exactly: a fixed backdrop appended to a
 * host element, backdrop-click-to-close, a `data-action` delegated click
 * handler, and a `close()` that is safe to call twice.
 *
 * Ownership is presentational here only — the server re-validates every equip
 * (TitleService.equip), so a locked row that somehow got clicked still fails
 * server-side rather than granting a free premium title.
 */

/** Band ordering and copy for the picker's section headers. */
const SECTIONS: { rarity: TitleRarity; heading: () => string; blurb: () => string }[] = [
  { rarity: 'MISSION', heading: () => t('profile.picker.section.MISSION'), blurb: () => t('profile.picker.section.MISSION.blurb') },
  { rarity: 'COMMON', heading: () => t('profile.picker.section.COMMON'), blurb: () => t('profile.picker.section.COMMON.blurb') },
  { rarity: 'RARE', heading: () => t('profile.picker.section.RARE'), blurb: () => '' },
  { rarity: 'EPIC', heading: () => t('profile.picker.section.EPIC'), blurb: () => '' },
  { rarity: 'LEGENDARY', heading: () => t('profile.picker.section.LEGENDARY'), blurb: () => '' },
];

/**
 * Tint per rarity, fed to `title-plate`'s `--title-tint` and the picker's own
 * rarity dots. Deliberately drawn from the existing theme tokens rather than
 * introducing new colours — Martian rust warms as rarity climbs, and
 * Legendary is the one that reaches full gold-bright.
 */
export const RARITY_TINT: Record<TitleRarity, string> = {
  MISSION: 'var(--color-purple)',
  COMMON: 'var(--color-text-dim)',
  RARE: 'var(--color-purple-light)',
  EPIC: 'var(--color-gold)',
  LEGENDARY: 'var(--color-gold-bright)',
};

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

export interface TitlePickerHandle {
  close: () => void;
  /** Ends a title scene that is still playing (the player left the screen). Safe when none is. */
  cancelScene: () => void;
}

export interface TitlePickerOptions {
  titles: TitleView[];
  /** Fired after a successful equip so the card can repaint without a refetch. */
  onEquipped: (titleId: string | null) => void;
  /** Fired when the player taps a locked premium title's price — jumps to the shop. */
  onBrowseShop?: () => void;
  /** Shown on the card in the equip scene (the same facts as the profile's player card). */
  playerName?: string;
  playerEmail?: string;
  enlisted?: string;
  power?: string;
}

export function openTitlePicker(root: HTMLElement, options: TitlePickerOptions): TitlePickerHandle {
  const { titles, onEquipped, onBrowseShop, playerName, playerEmail, enlisted, power } = options;
  let closed = false;
  let busy = false;
  let sceneCancel: (() => void) | null = null;

  // The scene module (and its sounds and card art) is lazy: fetched when the picker opens, so the
  // first scene is not late — and not at all until someone opens the picker.
  void import('./title-scenes').then((m) => m.preloadTitleScenes()).catch(() => {});

  const el = document.createElement('div');
  el.className = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', t('profile.picker.label'));

  function rowHtml(title: TitleView): string {
    const tint = RARITY_TINT[title.rarity];
    const selectable = title.owned;
    const state = title.equipped
      ? 'border-gold bg-gold/10'
      : selectable
        ? 'border-border hover:border-gold/70'
        : 'border-border/40 opacity-55';

    // A locked row explains WHY it is locked — the mission requirement, or
    // the Delta-V price — rather than just being dimmed.
    const trailing = title.equipped
      ? `<span class="text-[10px] text-gold uppercase tracking-widest shrink-0">${t('profile.picker.worn')}</span>`
      : selectable
        ? `<span class="text-[10px] text-text-dim uppercase tracking-widest shrink-0">${t('profile.picker.select')}</span>`
        : title.source === 'PREMIUM'
          ? `<span class="text-[11px] text-gold tabular-nums shrink-0">${title.price} &#9650;v</span>`
          : `<span class="text-[10px] text-text-dim shrink-0">${t('profile.picker.locked')}</span>`;

    const sub = title.owned
      ? ''
      : `<div class="text-[10px] text-text-dim truncate">${escapeHtml(title.requirement ?? t('profile.picker.inShop'))}</div>`;

    return `
      <button type="button"
        data-action="${selectable ? 'equip' : title.source === 'PREMIUM' ? 'buy' : 'noop'}"
        data-title-id="${title.id}"
        ${selectable || title.source === 'PREMIUM' ? '' : 'disabled'}
        aria-pressed="${title.equipped}"
        class="w-full min-h-11 flex items-center gap-2.5 px-3 py-2 rounded border text-left transition-colors duration-[120ms] touch-manipulation ${state}">
        <span class="w-2 h-2 rounded-full shrink-0" style="background:${tint}" aria-hidden="true"></span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm text-text truncate">${escapeHtml(title.label)}</span>
          ${sub}
        </span>
        ${trailing}
      </button>
    `;
  }

  function sectionHtml(section: (typeof SECTIONS)[number]): string {
    const rows = titles.filter((x) => x.rarity === section.rarity);
    if (rows.length === 0) return '';
    const ownedCount = rows.filter((x) => x.owned).length;
    return `
      <section class="flex flex-col gap-1.5">
        <div class="flex items-baseline justify-between gap-2">
          <h3 class="font-heading text-[11px] text-gold uppercase tracking-[0.18em]">${section.heading()}</h3>
          <span class="text-[10px] text-text-dim tabular-nums">${ownedCount}/${rows.length}</span>
        </div>
        ${section.blurb() ? `<p class="text-[10px] text-text-dim -mt-1">${section.blurb()}</p>` : ''}
        <div class="flex flex-col gap-1">${rows.map(rowHtml).join('')}</div>
      </section>
    `;
  }

  el.innerHTML = `
    <div data-modal-panel
      class="popup-enter w-full sm:max-w-lg max-h-[88vh] flex flex-col bg-surface border border-border border-t-2 border-t-gold rounded-t-xl sm:rounded-xl">
      <div class="shrink-0 flex items-start justify-between gap-3 p-4 pb-3 border-b border-border">
        <div class="min-w-0">
          <h2 class="font-heading text-gold text-base">${t('profile.picker.heading')}</h2>
          <p class="text-xs text-text-dim">${t('profile.picker.subtitle')}</p>
        </div>
        <button type="button" data-action="close" aria-label="${t('common.close')}"
          class="min-w-11 min-h-11 -m-2 flex items-center justify-center text-text-dim hover:text-text leading-none text-xl shrink-0">&times;</button>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 flex flex-col gap-4">
        <button type="button" data-action="clear"
          class="w-full min-h-11 px-3 rounded border border-border text-text-dim text-sm hover:border-gold hover:text-text transition-colors duration-[120ms] touch-manipulation">
          ${t('profile.picker.clear')}
        </button>
        ${SECTIONS.map(sectionHtml).join('')}
      </div>
    </div>
  `;

  el.addEventListener('click', (e) => {
    if (e.target === el) close();
  });
  root.appendChild(el);

  function close(): void {
    if (closed) return;
    closed = true;
    el.remove();
  }

  async function apply(titleId: string | null): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await equipTitle(titleId);
      // The header's badge under the player name re-reads straight away, not on the next tab switch.
      window.dispatchEvent(new Event('title:changed'));
      if (closed) return;
      // State first, so the card is right even if the scene is skipped or cut short.
      onEquipped(titleId);
      close();
      const chosen = titleId ? titles.find((x) => x.id === titleId) : undefined;
      const toast = () => showToast(titleId ? t('profile.picker.toast.updated') : t('profile.picker.toast.cleared'), 'success');
      if (!chosen) {
        toast();
        return;
      }
      // The scene plays over the screen; the toast waits for it rather than stacking on top.
      try {
        const { playTitleScene } = await import('./title-scenes');
        const scene = playTitleScene({ rarity: chosen.rarity, title: chosen.label, player: playerName ?? '', email: playerEmail, enlisted, power });
        sceneCancel = scene.cancel;
        await scene.done;
      } catch {
        // A scene is a flourish; failing to load it must never undo an equip.
      }
      sceneCancel = null;
      (document.querySelector('[data-action="open-titles"]') as HTMLElement | null)?.focus();
      toast();
    } catch (err) {
      busy = false;
      if (closed) return;
      showToast((err as Error).message, 'error');
    }
  }

  el.addEventListener('click', (e) => {
    const actionEl = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    if (action === 'close') close();
    else if (action === 'clear') void apply(null);
    else if (action === 'equip') void apply(actionEl.dataset.titleId ?? null);
    else if (action === 'buy') void askToBuy(actionEl.dataset.titleId ?? '');
  });

  /**
   * A locked premium title: instead of bouncing the player to the shop, ask right here —
   * "<title> costs <n> Delta-V. Buy?" — and, on yes, pay, unlock and equip it in one go.
   */
  async function askToBuy(titleId: string): Promise<void> {
    const title = titles.find((x) => x.id === titleId);
    if (!title || title.price == null || busy) return;
    const price = title.price;
    const prompt = document.createElement('div');
    prompt.className = 'absolute inset-0 z-10 flex items-center justify-center bg-black/70 p-4';
    prompt.setAttribute('role', 'alertdialog');
    prompt.setAttribute('aria-label', t('profile.picker.buy.aria', { title: title.label }));
    prompt.innerHTML = `
      <div class="w-full max-w-xs flex flex-col gap-3 p-4 rounded-lg border border-gold/50 bg-surface-2 shadow-lift">
        <p class="text-sm text-text leading-relaxed" data-buy-text>${t('profile.picker.buy.question', { title: `<strong class="text-gold-bright">${escapeHtml(title.label)}</strong>`, price: String(price) })}</p>
        <p class="text-[11px] text-text-dim" data-buy-balance>&nbsp;</p>
        <div class="flex gap-2">
          <button type="button" data-buy="no" class="flex-1 min-h-11 px-3 rounded border border-border text-text-dim text-sm hover:text-text hover:border-gold touch-manipulation">${t('profile.picker.buy.no')}</button>
          <button type="button" data-buy="pay" disabled class="deltav-gold flex-1 min-h-11 px-3 rounded border font-button text-sm disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation">${t('profile.picker.buy.pay', { price: String(price) })}</button>
        </div>
      </div>`;
    const panel = el.querySelector('[data-modal-panel]') as HTMLElement;
    (panel.classList.contains('relative') ? panel : (panel.style.position = 'relative', panel)).appendChild(prompt);
    const payBtn = prompt.querySelector('[data-buy="pay"]') as HTMLButtonElement;
    const balanceEl = prompt.querySelector('[data-buy-balance]') as HTMLElement;
    let paying = false;

    prompt.addEventListener('click', (e) => {
      e.stopPropagation();
      const which = (e.target as HTMLElement).closest('[data-buy]')?.getAttribute('data-buy');
      if (which === 'no') prompt.remove();
      else if (which === 'shop') {
        close();
        onBrowseShop?.();
      }
      else if (which === 'pay') void pay();
    });

    try {
      const { premium } = await fetchStoreBalance();
      if (closed) return;
      const enough = premium >= price;
      balanceEl.textContent = enough ? t('profile.picker.buy.balance', { n: String(premium) }) : t('profile.picker.buy.notEnough', { n: String(premium) });
      balanceEl.classList.toggle('text-error', !enough);
      if (!enough && onBrowseShop) {
        const more = document.createElement('button');
        more.type = 'button';
        more.dataset.buy = 'shop';
        more.className = 'ml-2 underline text-gold hover:text-gold-bright';
        more.textContent = t('profile.picker.buy.getMore');
        balanceEl.appendChild(more);
      }
      payBtn.disabled = !enough;
    } catch {
      // Balance unknown: let the server decide — it refuses an underfunded purchase.
      payBtn.disabled = false;
    }

    async function pay(): Promise<void> {
      if (paying) return;
      paying = true;
      payBtn.disabled = true;
      try {
        await purchaseTitle(titleId);
        if (closed) return;
        title!.owned = true;
        window.dispatchEvent(new Event('deltav:refresh'));
        prompt.remove();
        await apply(titleId);
      } catch (err) {
        paying = false;
        payBtn.disabled = false;
        if (!closed) showToast((err as Error).message, 'error');
      }
    }
  }

  return {
    close,
    cancelScene: () => sceneCancel?.(),
  };
}
